<template>
  <el-dialog
    :visible.sync="dialogVisible"
    title="角色关联用户"
    v-bind="$attrs"
    :destroy-on-close="true"
    :close-on-click-modal="false"
  >
    <div class="dialog-around h-60">
      <!-- 组织树 -->
      <rc-tree
        class="w-1/5 mr-3"
        :title="'部门树'"
        :tree-data="treeData"
        :default-expanded-keys="defaultExpandedKeys"
        :props="treeProps"
        :current-node-key="currentNodeKey"
        @node-click="handleNodeClick"
      >
        <span slot-scope="{ node }">
          <svg-icon icon-class="custom-org" class="mr-1" />
          <span>{{ node.label }}</span>
        </span>
      </rc-tree>

      <!-- 组织下用户 -->
      <rc-ge-table
        ref="rcGeTable"
        row-key="id"
        :data="tableData"
        :columns="tableColumns"
        :checkbox="true"
        :reserve-selection="true"
        :pagination="pagination"
        :operable="false"
        class="w-3/5"
        @tableSearch="searchTable"
        @handleCurrentChange="handlePaginationChange"
        @handleSizeChange="handlePaginationChange"
        @selection-change="selectionChange"
      ></rc-ge-table>

      <!-- 用户已选择 -->
      <rc-object-selected
        class="w-1/5 ml-3"
        :data="selectList"
        @clearAllSelect="clearAllSelect"
        @deleteSelect="deleteSelect"
      ></rc-object-selected>
    </div>

    <span slot="footer" class="text-right">
      <el-button @click="dialogVisible = false">取 消</el-button>
      <el-button type="primary" @click="assignRole">确 定</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import RcTree from "@/views/standard/business/components/RcTree";
import RcGeTable from "@/views/standard/business/components/RcGeTable";
import RcObjectSelected from "@/views/standard/business/components//RcObjectSelected";
import userApi from "@/api/user/user";
import roleApi from "@/api/role/role";

export default {
  name: "MetaUserDialog",
  components: { RcTree, RcGeTable, RcObjectSelected },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    userIds: {
      type: Array,
      default: () => []
    },
    roleId: {
      type: [String, Number],
      default: ""
    }
  },
  data() {
    return {
      dialogVisible: false,
      // 左边树
      currentNodeKey: undefined,
      treeProps: { label: "orgName" },
      treeData: [],
      defaultExpandedKeys: [],
      // 表格
      pagination: {
        pageNum: 1,
        total: 0,
        pageSize: 10
      },
      tableSearchWord: "",
      tableData: [],
      tableColumns: [
        {
          label: "用户名",
          key: "username"
        },
        {
          label: "姓名",
          key: "displayName"
        },
        {
          label: "手机号",
          key: "cellphone"
        }
      ],
      // 已选择的
      selectList: [],
      lock: false
    };
  },
  computed: {
    ...mapGetters(["tenantId"]),
    checkedKeys() {
      return this.selectList.map(item => item.id);
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        this.dialogVisible = val;
        val && this.initData();
      }
    },
    dialogVisible(val) {
      this.$emit("update:visible", val);
    }
  },
  methods: {
    initData() {
      // 初始化树 && 根据树节点初始化表格
      this.initTree().then(() => this.initTable());

      // 初始化已选择list
      this.initObjectSelected();
    },
    async initTree() {
      const params = { parseToTree: true };
      const { data } = await userApi.getOrgList(this.tenantId, params).catch(() => {});
      // 过滤掉当前组织
      this.treeData = [data];
      this.defaultExpandedKeys = [data.id];
      this.currentNodeKey = data.id;
    },
    async initTable() {
      this.tableData = [];
      const params = {
        size: this.pagination.pageSize,
        page: this.pagination.pageNum,
        search: this.tableSearchWord,
        orgId: this.currentNodeKey,
        "orders[0].asc": "false",
        "orders[0].column": "createTime"
      };
      const {
        data: { records = [], total = 0 }
      } = await userApi.getUserList(params).catch(() => {});
      this.tableData = records;
      this.pagination.total = Number(total);

      // 默认选中
      this.toggleTableSelection();
    },

    toggleTableSelection(select = true) {
      // 默认选中
      this.$nextTick(() => {
        this.lock = true;
        this.tableData.forEach(item => {
          if (this.checkedKeys.includes(item.id)) {
            this.$refs.rcGeTable.toggleRowSelection(item, select);
          }
        });
        this.lock = false;
      });
    },
    async initObjectSelected() {
      if (!Array.isArray(this.userIds) || !this.userIds.length) {
        this.selectList = [];
        return;
      }

      // 处理默认选中的值
      const { data = [] } = await userApi.getUserListByIds(this.userIds).catch(() => {});

      // 初始化选中的值
      this.selectList = Array.isArray(data)
        ? data.map(item => ({ id: item.id, name: item.displayName }))
        : [];
      this.toggleTableSelection();
    },
    // 表格搜索
    searchTable(keyword) {
      this.tableSearchWord = keyword;
      this.initTable();
    },
    handlePaginationChange(data) {
      this.$set(this, "pagination", data);
      this.initTable();
    },
    // 树节点点击
    handleNodeClick(node) {
      const { id } = node;
      this.currentNodeKey = id;
      this.$set(this, "pagination", { pageNum: 1, total: 0, pageSize: 10 });
      this.initTable();
    },
    clearAllSelect() {
      this.selectList = [];
      this.$refs.rcGeTable.clearSelection();
    },
    deleteSelect({ id }) {
      this.selectList = this.selectList.filter(item => item.id !== id);
      this.$nextTick(() => {
        this.tableData.forEach(item => {
          if (item.id === id) {
            this.$refs.rcGeTable.toggleRowSelection(item, false);
          }
        });
      });
    },
    selectionChange(list) {
      if (this.lock) {
        return;
      }
      // 选中回显
      list.forEach(item => {
        // 查找元素
        const findItem = this.selectList.find(select => select.id === item.id);

        if (!findItem) {
          this.selectList.push({ id: item.id, name: item.displayName });
        }
      });

      // 取消选中去除
      this.tableData.forEach(item => {
        // 过滤掉已经筛选的
        const selectItem = list.find(select => item.id === select.id);

        // 取消选择 & 在选中数据中
        if (!selectItem) {
          this.selectList = this.selectList.filter(select => select.id !== item.id);
        }
      });
    },
    // 分配角色权限
    async assignRole() {
      const keys = this.selectList.map(item => item.id);
      const params = { operation: "REPLACE", keys };
      const { success } = await roleApi.roleAssignUser(this.roleId, params).catch(() => {});

      if (success) {
        this.$message.success("更新成功！");
      }

      this.dialogVisible = false;
      this.$emit("confirm");
    }
  }
};
</script>
